import {Component, EventEmitter, Input, Output, ViewChild, OnDestroy}  from '@angular/core';
import {NgbTypeaheadConfig, NgbTypeaheadSelectItemEvent}    from "@ng-bootstrap/ng-bootstrap";

import {AngularQuery}                                       from "../../services/query/angularquery";
import {ItemComponent}                                      from "../../articles/item/item.component";
import {Permissions}                                        from "../../services/permissions/permissions";
import {TagService}                                         from "../../services/tag/tag.service";
import {Tag}                                                from '../../services/tag/tag';

import {Observable}                                         from "rxjs/Observable";
import {_do}                                                from "rxjs/operator/do";
import {switchMap}                                          from "rxjs/operator/switchMap";
import {distinctUntilChanged}                               from "rxjs/operator/distinctUntilChanged";
import {debounceTime}                                       from "rxjs/operator/debounceTime";
import {_catch}                                             from "rxjs/operator/catch";
import {of}                                                 from "rxjs/observable/of";
import {isNullOrUndefined}                                  from "util";
import {Subscription}                                       from "rxjs/Subscription";


@Component({
  selector: 'tag-typeahead',
  providers: [TagService, NgbTypeaheadConfig],
  template: `
      <div [class.has-danger]="searchFailed">

          <label class="font-weight-bold" for="tag-typeahead-http">Tag  [
              <span *ngIf="! searching">*</span>
              <span *ngIf="searching">
                    <img src="assets/typeahead.gif" style="width: 16px; margin-top: 2px; cursor: default;" />
                </span>
              ]</label>

	      <div class="input-group">

              <input #typeaheadTagInput
                     id="tag-typeahead-http"
                     type="text"
                     class="form-control"
                     [ngClass]="setSize()"
                     [ngbTypeahead]="tagSearch"
                     placeholder="Search by tag name"
                     [resultFormatter]="resultFormatter"
                     [inputFormatter]="inputFormatter"
                     (selectItem)="tagSelectedEvent($event)"
              />

              <span class="input-group-btn">
		      
			      <button class="btn btn-success" 
			              type="button"
			              (click)="addTag()"
			              [hidden]="hideAddButton()"
                          [disabled]="false === searchFailed || typeaheadTagInput.value.length < 2"
			      >
				      Add
			      </button>
	    
		      </span>

          </div>

          <div class="form-control-feedback" *ngIf="searchFailed">No choices found</div>

      </div>`,
})


/**
 * Handle typeahead searching for Tags.
 */
export class TagTypeaheadComponent implements OnDestroy {

	// These are semaphores used to hide or display messages.
	searchFailed = false;
	searching = false;
	tagFound:Subscription;


	/**
	 * Initialize the component.
	 *
	 * @param tagService  The TagService used to retrieve and save Tag information.
	 * @param config      Use the NgbTypeaheadConfig to allow non-list choices to be used.
	 */
	constructor(private tagService: TagService, config:NgbTypeaheadConfig) {

		config.editable = true;

	}


	/**
	 * Close the subscription.
	 */
	ngOnDestroy(): void {

		if (this.tagFound) {

			this.tagFound.unsubscribe();

		}

	}


	@Input()
	public size:string;  // The size of the components.  This comes in from the parent page.


	@Input()
	public suppressTagDisplay:boolean = false;  // Whether to display the selected tag in the INPUT field when the user makes a choice.


	@Input()
	public tag_id:number;  // The tag ID of the value from the host page.


	/**
	 * Controls the Tag choices INPUT.
	 */
	@ViewChild('typeaheadTagInput')
	typeaheadTagInput: any;


	@Output()
	public onTagChange = new EventEmitter<Tag>();  // This notifies the parent page a Tag has been selected.


	/**
	 * Target for the 'Add' button.  This adds the selected tag to the tag list and fills in
	 * the fields on the parent page.
	 */
	addTag():void {

		// Hide the failed search.
		this.searchFailed = false;

		// Make sure there are no illegal characters in the tag.
		this.typeaheadTagInput.nativeElement.value = ItemComponent.replaceWordCharacters(this.typeaheadTagInput.nativeElement.value);

		// Create the JSON object for post.
		let json:object = {};
		json['tag'] = this.typeaheadTagInput.nativeElement.value;
		json['id'] = ( 0 >= this.tag_id ) ? null : this.tag_id;

		// Create the post query.
		let query:AngularQuery = new AngularQuery();
		query.useCacheBuster(false);
		query.useParams(false);

		if ( 0 >= this.tag_id ) {

			query.setEndpoint( AngularQuery.ENDPOINT_TAG, 'new' );

		} else {

			query.setEndpoint( AngularQuery.ENDPOINT_TAG, this.tag_id.toString() );

		}

		// Unsubscribe from any existing query.
		if (this.tagFound) {

			this.tagFound.unsubscribe();

		}

		// Save the tag and get back the updated data.
		this.tagFound = this.tagService.save(query.getUrl(), JSON.stringify(json)).subscribe(

			(response) => {

				let t:Tag = new Tag(response);
				this.onTagChange.emit(t);

			},
			() => {

				window.alert('Unable to save tag');

			}

		);

	}


	/**
	 * Only administrators can use the 'Add' button on this
	 * component.
	 *
	 * @return {boolean}  False if the user is an administrator, true otherwise.
	 */
	hideAddButton():boolean {

		return ! Permissions.checkPermission(Permissions.SUPER_USER);

	}


	/**
	 * Used by the typeahead to get the tag's 'tag' field
	 * and display it as the choice in the typeahead's INPUT
	 * field when the choice is made.  If suppressTagDisplay
	 * is true, a zero-length string is returned; otherwise,
	 * the tag is displayed.
	 *
	 * @param result  The Tag object selected by the user.
	 */
	inputFormatter = (result: Tag) => {
		return (this.suppressTagDisplay) ? '' : result.tag;
	};


	/**
	 * Used by the typeahead to get the tag's 'tag' field
	 * so it can be displayed as a choice to be selected
	 * by the user.
	 *
	 * @param result  The Tag object selected by the user.
	 */
	resultFormatter = (result: Tag) => result.tag;


	/**
	 * Read the size variable passed in from the hosting template
	 * and set the controls based on that.
	 *
	 * If size is null, '' is returned.  The other choices are
	 * 'sm' and 'lg' versions of 'form-control'.
	 *
	 * @returns {string}  The appropriate Bootstrap value for the form type.
	 */
	setSize(): string {

		if (isNullOrUndefined(this.size)) {

			return '';

		}

		if (this.size.toLowerCase().startsWith('s')) {

			return 'form-control-sm';

		}

		if (this.size.toLowerCase().startsWith('l')) {

			return 'form-control-lg';

		}

		return '';

	}


	/**
	 * The typeahead puts the search term into the
	 * TagService and processes the results.  It also
	 * sets the semaphores to show/hide typeahead
	 * progress.
	 *
	 * @return The Tag Observable that will return data or errors.
	 */
	tagSearch = (tag: Observable<Tag[]>) =>

		_do.call(

			switchMap.call(

				_do.call(
					distinctUntilChanged.call(
						debounceTime.call(tag, 300)),
					() => {

						this.searching = true;

					}
				),

				(search: string) =>
					_catch.call(
						_do.call(
							this.tagService.search(search),
							(tags: Tag[]) => {
								this.searchFailed = 0 === tags.length;
								this.searching = false;
							}),

							() => {

								// Report the search failure.
								this.searchFailed = true;
								this.searching = false;
								return of.call([]);

							})
			),

			() => this.searching = false

		);


	/**
	 * The typeahead calls this method when the user makes
	 * a selection.  This notifies the parent page with an
	 * event that passes the data.
	 *
	 * @param selection  The event that holds the Tag choice made by the user.
	 */
	tagSelectedEvent(selection: NgbTypeaheadSelectItemEvent): void {

		this.onTagChange.emit(selection.item);

	}


}



// WEBPACK FOOTER //
// ./src/app/typeaheads/tag/tag-typeahead.component.ts